import * as Sentry from '@sentry/react'
import { useEffect } from 'react'
import { Navigate, Outlet, useNavigate } from 'react-router'
import { useLoginRedirectTo } from '@/components/login/useLoginRedirectTo'
import { ALLOWED_LOGIN_EXTERNAL_REDIRECT } from '@/config/main'
import { useDocumentVisibleAfterDelay } from '@/hooks/useDocumentVisibleAfterDelay'
import { useSession } from '@/session/SessionContext'
import { RouterPath } from './paths'

const allowedExternalRedirects = (ALLOWED_LOGIN_EXTERNAL_REDIRECT || '')
  .split(',')
  .filter(Boolean)

type NavigateExternalProps = {
  url: string
}

const NavigateExternal = ({ url }: NavigateExternalProps) => {
  const navigate = useNavigate()

  useEffect(() => {
    if (allowedExternalRedirects.includes(url)) {
      window.location.replace(url)
    } else {
      void navigate(RouterPath.root)

      Sentry.captureMessage('External redirect not allowed', {
        level: 'warning',
        extra: {
          url
        }
      })
    }
  }, [url, navigate])

  return null
}

export const PublicAuthRoute = () => {
  const { isAuthenticated, isPasswordExpired, refreshSession } = useSession()
  const redirectTo = useLoginRedirectTo()
  const isExternalRedirect = /^https?:\/\//.test(redirectTo)

  useDocumentVisibleAfterDelay(() => {
    void refreshSession()
  }, 8000)

  if (isPasswordExpired) {
    return (
      <Navigate to={`${RouterPath.changePassword}?reason=expired`} replace />
    )
  }

  if (isAuthenticated) {
    return isExternalRedirect ? (
      <NavigateExternal url={redirectTo} />
    ) : (
      <Navigate to={redirectTo} replace />
    )
  }

  return <Outlet />
}
